import React, { useState } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getAuthHeader } from '@/utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export const ImageUploader = ({ images = [], onChange, category = 'listings' }) => {
  const [uploading, setUploading] = useState(false);
  const [previews, setPreviews] = useState(images);

  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error(`Type de fichier non autorisé. Formats acceptés : JPG, PNG, WebP`);
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Fichier trop volumineux. Taille maximale : 5 MB`);
      return false;
    }

    return true;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (!files || files.length === 0) return;

    // Check if adding these files would exceed max
    const totalImages = previews.length + files.length;
    if (totalImages > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images autorisées`);
      return;
    }

    setUploading(true);

    try {
      const uploadedUrls = [];

      for (const file of files) {
        // Validate file
        if (!validateFile(file)) {
          continue;
        }

        // Create FormData
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);

        // Upload
        const response = await axios.post(
          `${BACKEND_URL}/api/upload-image`,
          formData,
          {
            headers: {
              ...getAuthHeader(),
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (response.data.success) {
          uploadedUrls.push(response.data.image_url);
        }
      }

      if (uploadedUrls.length > 0) {
        const newPreviews = [...previews, ...uploadedUrls];
        setPreviews(newPreviews);
        onChange(newPreviews);
        toast.success(`${uploadedUrls.length} image(s) téléversée(s) avec succès`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleRemove = async (imageUrl) => {
    try {
      // Remove from preview
      const newPreviews = previews.filter(url => url !== imageUrl);
      setPreviews(newPreviews);
      onChange(newPreviews);

      // Delete from server
      await axios.delete(`${BACKEND_URL}/api/delete-image`, {
        params: { image_url: imageUrl },
        headers: getAuthHeader(),
      });

      toast.success('Image supprimée');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">
          Images ({previews.length}/{MAX_IMAGES})
        </label>
        {previews.length < MAX_IMAGES && (
          <label htmlFor="image-upload" className="cursor-pointer">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#127abb] text-[#127abb] hover:bg-primary-50"
              disabled={uploading}
              onClick={() => document.getElementById('image-upload').click()}
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Téléversement...' : 'Téléverser'}
            </Button>
            <input
              id="image-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {/* Preview Grid */}
      {previews.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {previews.map((imageUrl, index) => (
            <div
              key={index}
              className="relative group aspect-video bg-slate-100 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#127abb] transition-colors"
            >
              <img
                src={imageUrl}
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemove(imageUrl)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <X size={16} />
              </button>
              {index === 0 && (
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-[#127abb] text-white text-xs rounded-full font-medium">
                  Image principale
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
          <ImageIcon className="mx-auto h-12 w-12 text-slate-400 mb-3" />
          <p className="text-sm text-slate-600 mb-2">Aucune image téléversée</p>
          <p className="text-xs text-slate-500">
            Formats acceptés : JPG, PNG, WebP (max 5 MB par image)
          </p>
        </div>
      )}

      {previews.length >= MAX_IMAGES && (
        <p className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg">
          ⚠️ Limite maximale atteinte ({MAX_IMAGES} images). Supprimez une image pour en ajouter une nouvelle.
        </p>
      )}
    </div>
  );
};

export default ImageUploader;